// Main entry — particle engine + scroll morph + DOM population + chart init
import { createEngine } from './engine/engine.js';
import { COMPANIES, CLUSTER_CITIES } from './data/companies.js';
import { OPPORTUNITIES } from './data/opportunities.js';
import { initOpportunityChart } from './chart.js';

// ---- COMPONENT DEPENDENCY DATA ----
const DEP_DATA = [
  { name: 'Actuators', high: 5, med: 14, low: 1 },
  { name: 'Hands / Dexterity', high: 1, med: 9, low: 2 },
  { name: 'Power', high: 2, med: 7, low: 0 },
  { name: 'Compute / Electronics', high: 6, med: 7, low: 1 },
  { name: 'Sensors', high: 2, med: 4, low: 1 },
  { name: 'Structure / Body', high: 6, med: 5, low: 1 },
];

// ---- SUPPLIER DATA ----
const SUPPLIERS = [
  ['CNC Machining', 'Plethora', 'SF Dogpatch', 'Automated 3/5-axis CNC, 23+ materials', 'Known robotics supplier'],
  ['CNC Machining', 'Fictiv HQ', 'San Francisco', 'Managed mfg: CNC, die cast, injection', 'Robotics vertical'],
  ['CNC Machining', 'AJ Solutions', 'Fremont', 'Prototype CNC mill/turn', 'Robotics prototypes'],
  ['CNC Machining', 'RivCut', 'San Mateo', 'CNC for robotics, no minimums', 'Peninsula-local'],
  ['Gears & Gearboxes', 'American Precision Gear', 'San Carlos', 'Custom gears since 1956; miniature gears', 'Robotics listed'],
  ['Gears & Gearboxes', 'Harmonic Drive (reps)', 'Bay Area reps', 'Strain-wave gears', 'Industry standard'],
  ['Motors / Winding', 'Silicon Valley Elec. Motor', 'Palo Alto', 'Servo/BLDC rewinding', 'Actuator maintenance'],
  ['PCB / EMS', 'Sanmina', 'San Jose', 'Tier-1 EMS: PCBA, enclosures', 'Full box-build'],
  ['PCB / EMS', 'Flex', 'Milpitas', 'Tier-1 EMS/ODM Lighthouse', 'Robotics programs'],
  ['PCB / EMS', 'Jabil', 'San Jose/Fremont', 'Tier-1 EMS', 'Robotics vertical'],
  ['PCB / EMS', 'Sierra Circuits', 'Sunnyvale', 'PCB fab+assembly, 5-day, rigid-flex', 'Quick-turn default'],
  ['PCB / EMS', 'Bay Area Circuits', 'Fremont', 'PCB fab+assembly since 1975', 'Quick-turn local'],
  ['PCB / EMS', 'Green Circuits', 'San Jose', 'PCBA + cable + box build', 'Robotics PCB stated'],
  ['Sheet Metal', 'M-T Metal Fabrication', 'Santa Clara', 'Laser cut, stamping, since 1950', 'OEM high-tech'],
  ['Injection Molding', 'Control Plastics', 'San Jose', 'Custom molding + metal, DFM', 'Medical/robot'],
  ['Injection Molding', 'Computer Plastics', 'Hayward', 'Advanced molding since 1969', 'Tooling + prod'],
  ['Wire Harness', 'Y.C. Cable', 'Fremont', 'Custom harnesses since 1985', 'Robot-grade'],
  ['Wire Harness', 'Compass Made', 'Fremont', 'Harness + electromechanical', 'Warm Springs'],
  ['Battery Packs', 'Lithos Energy', 'Hayward', 'Modular Li-ion rugged packs', 'Robot-grade'],
  ['Optics / Camera', 'Optopax', 'Bay Area', 'AI camera modules UV-LWIR', 'Robotics vision'],
  ['Adhesives / Thermal', 'Boyd Corporation', 'San Jose+Pleasanton', 'TIMs, liquid cooling, gaskets', 'Robot thermal'],
  ['Fasteners / Bearings', 'Motion Industries', 'Santa Clara+Hayward', 'Bearings, gear reducers', 'Same-day BOM'],
  ['Contract Mfg', 'Flex/Zollner/Jabil', 'Milpitas/SJ', 'Full robot assembly', 'Robotics programs'],
];

// ---- INITIALIZATION ----
async function main() {
  setupReveal();

  // Populate DOM elements
  populateCompanies();
  populateDependencies();
  populateSuppliers();
  populateOpportunities();

  // Initialize particle engine
  const canvas = document.getElementById('hero-canvas');
  if (!canvas) return;

  try {
    const engine = await createEngine(canvas, 30000);
    if (!engine || !engine.ok) {
      console.warn('[main] Engine init failed, hiding canvas');
      canvas.style.display = 'none';
      return;
    }

    engine.start();
    console.log(`[main] Engine active: ${engine.backend}`);

    // Scroll → morph driver. Scenes are scroll-depth-triggered:
    // Scene 0 (0vh–60vh): Robot silhouette — "this is what we're building"
    // Scene 1 (60vh–120vh): Node graph — "the supply chain connects everything"
    // Scene 2 (120vh–180vh): Bay Area map — "here's where it all is"
    // Scene 3 (180vh–260vh): BOM bars — "this is what goes into one" (colored by China-dependence)
    // Scene 4 (260vh+): Constellation — "these are the opportunities"
    let lastScene = -1;
    const sceneTriggers = [
      { sel: '#cluster', scene: 1 },        // company cards section → graph
      { sel: '#dependencies', scene: 3 },   // BOM section → BOM bars
      { sel: '#opportunities', scene: 4 },  // chart section → constellation
    ];

    function updateScene() {
      const scrollY = window.scrollY;
      const vh = window.innerHeight;
      let scene = 0;

      // Scene 0→1: scroll past hero (0→0.6 viewport)
      if (scrollY > vh * 0.55) scene = 1;
      // Scene 1→2: scroll into first section (cluster)
      const clusterEl = document.querySelector('#cluster');
      if (clusterEl) {
        const r = clusterEl.getBoundingClientRect();
        if (r.top < vh * 0.75) scene = 2;
      }
      // Scene 2→3: BOM section in view
      const depEl = document.querySelector('#dependencies');
      if (depEl) {
        const r = depEl.getBoundingClientRect();
        if (r.top < vh * 0.75) scene = 3;
      }
      // Scene 3→4: opportunities section in view
      const oppEl = document.querySelector('#opportunities');
      if (oppEl) {
        const r = oppEl.getBoundingClientRect();
        if (r.top < vh * 0.75) scene = 4;
      }

      scene = Math.max(0, Math.min(4, scene));
      if (Math.abs(scene - lastScene) > 0.005) {
        engine.setScene(scene);
        lastScene = scene;
      }
    }

    window.addEventListener('scroll', updateScene, { passive: true });
    updateScene();

    // Scene captions — tell the user what each particle shape means
    const CAPTIONS = {
      0: 'This is what we\'re building',
      1: 'Every part connects to every other part',
      2: 'The Bay Area cluster — San Carlos to Fremont',
      3: 'What goes into one robot — colored by China dependence',
      4: '12 opportunities — sized by market, colored by barrier',
    };
    const captionEl = document.getElementById('scene-caption');
    let captionTimer = null;
    let lastCaptionScene = -1;

    function updateCaption(scene) {
      if (!captionEl || scene === lastCaptionScene) return;
      lastCaptionScene = scene;
      const text = CAPTIONS[scene];
      if (!text) return;
      captionEl.textContent = text;
      captionEl.classList.add('visible');
      clearTimeout(captionTimer);
      captionTimer = setTimeout(() => captionEl.classList.remove('visible'), 3500);
    }

    // Wire caption into the scene observer
    const sceneObserver = new MutationObserver(() => {
      const s = parseFloat(captionEl?.dataset?.scene || '0');
      updateCaption(s);
    });

    // Poll caption updates (cheap, no DOM mutation)
    setInterval(() => {
      const s = lastScene;
      if (s >= 0) updateCaption(Math.round(s));
    }, 500);

    // Clean up on unload
    window.addEventListener('beforeunload', () => engine.destroy());
  } catch (e) {
    console.error('[main] Engine error:', e);
    canvas.style.display = 'none';
  }

  // Initialize Three.js chart (lazy — on scroll into view)
  let chartInitialized = false;
  const chartIO = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && !chartInitialized) {
      chartInitialized = true;
      initOpportunityChart('chart-container', OPPORTUNITIES);
      chartIO.disconnect();
    }
  }, { threshold: 0.15 });
  const chartContainer = document.getElementById('chart-container');
  if (chartContainer) chartIO.observe(chartContainer);
}

// ---- Scroll Reveal (IntersectionObserver fallback for Firefox) ----
function setupReveal() {
  const supportsScrollTimeline = CSS.supports('animation-timeline: view()');
  if (supportsScrollTimeline) return; // native CSS handles it

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-in');
      }
    }
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
}

// ---- Populate company cards ----
function populateCompanies() {
  const grid = document.getElementById('company-grid');
  if (!grid) return;

  COMPANIES.forEach(c => {
    const card = document.createElement('div');
    card.className = `company-card reveal${c.sanMateo ? ' san-mateo' : ''}`;
    card.innerHTML = `
      <div class="company-name">${c.name}</div>
      <div class="company-robot">${c.robot}</div>
      <div class="company-facility">🏭 ${c.facility}</div>
      <div class="company-scale">📊 ${c.scale}</div>
      <div class="company-note">${c.note}</div>
      <div class="company-badge ${c.sanMateo ? 'badge-local' : 'badge-nearby'}">
        ${c.sanMateo ? '◆ San Mateo County' : '● Bay Area'}
      </div>
    `;
    grid.appendChild(card);
  });
}

// ---- Populate dependency bars ----
function populateDependencies() {
  const grid = document.getElementById('dep-grid');
  if (!grid) return;

  DEP_DATA.forEach((d, i) => {
    const total = d.high + d.med + d.low;
    const highPct = Math.round(d.high / total * 100);
    const medPct = Math.round(d.med / total * 100);

    const group = document.createElement('div');
    group.className = 'dep-bar-group reveal';
    group.style.animationDelay = `${i * 0.08}s`;
    group.innerHTML = `
      <div class="dep-label"><span>${d.name}</span><span>${total} components</span></div>
      <div class="bar-track">
        <div class="bar-fill bar-high" style="width:${highPct}%"></div>
      </div>
      <div style="display:flex;gap:0;margin-top:2px;">
        <div class="bar-fill bar-high" style="width:${highPct}%;height:4px;border-radius:100px 0 0 100px;"></div>
        <div class="bar-fill bar-med" style="width:${medPct}%;height:4px;"></div>
      </div>
    `;
    grid.appendChild(group);
  });
}

// ---- Populate supplier list ----
function populateSuppliers() {
  const list = document.getElementById('supplier-list');
  if (!list) return;

  SUPPLIERS.forEach(s => {
    const item = document.createElement('div');
    item.className = 'supplier-item reveal';
    item.innerHTML = `
      <div class="supplier-category">${s[0]}</div>
      <div class="supplier-name">${s[1]}</div>
      <div class="supplier-city">📍 ${s[2]}</div>
      <div class="supplier-what">${s[3]}</div>
      <div style="font-size:0.72rem;color:var(--accent);margin-top:0.25rem;">${s[4]}</div>
    `;
    list.appendChild(item);
  });
}

// ---- Populate opportunity table ----
function populateOpportunities() {
  const table = document.getElementById('opp-table');
  if (!table) return;

  const thead = table.createTHead();
  thead.innerHTML = `<tr><th>#</th><th>Opportunity</th><th>Barrier</th><th>Strategic Value</th><th>How to Enter</th></tr>`;

  const tbody = table.createTBody();
  OPPORTUNITIES.forEach(o => {
    const barrierClass = o.barrier >= 7 ? 'barrier-high' : o.barrier >= 5 ? 'barrier-med' : 'barrier-low';
    const barrierLabel = o.barrier >= 7 ? 'HIGH' : o.barrier >= 5 ? 'MED' : 'LOW';
    const row = tbody.insertRow();
    row.innerHTML = `
      <td><span class="opp-rank">${o.rank}</span></td>
      <td><span class="opp-name">${o.name}</span><br><span class="opp-detail">${o.detail}</span></td>
      <td><span class="barrier-badge ${barrierClass}">${barrierLabel}</span></td>
      <td><span class="opp-value">${o.value}/10</span></td>
      <td class="opp-detail">${o.detail.split('. ')[0] || o.detail}</td>
    `;
  });
}

// Boot
main().catch(console.error);
